import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaShoppingCart, 
  FaCalculator, 
  FaCheck, 
  FaArrowRight, 
  FaList, 
  FaThLarge 
} from "react-icons/fa";
import Header from "../components/Header";
import Footer from "../components/Footer";
import api from "../axios/api";

const Catalogo = () => {
  const [naturezas, setNaturezas] = useState([]);
  const [nomes, setNomes] = useState([]);
  const [precos, setPrecos] = useState([]);
  const [itensCompletos, setItensCompletos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNaturezaId, setSelectedNaturezaId] = useState("");

  // Buscar todos os dados e juntar no Front-end
  useEffect(() => {
    let isMounted = true;
    const fetchData = async () => {
      try {
        const [resNaturezas, resNomes, resPrecos] = await Promise.all([
          api.get("/emolumento-naturezas"),
          api.get("/emolumento-nomes"),
          api.get("/emolumento-precos")
        ]);

        const dadosNaturezas = Array.isArray(resNaturezas.data) ? resNaturezas.data : (resNaturezas.data.data || []);
        const dadosNomes = Array.isArray(resNomes.data) ? resNomes.data : (resNomes.data.data || []);
        const dadosPrecos = Array.isArray(resPrecos.data) ? resPrecos.data : (resPrecos.data.data || []);

        if (isMounted) {
          setNaturezas(dadosNaturezas);
          setNomes(dadosNomes);
          setPrecos(dadosPrecos);

          // Cruzamento / Junção no Frontend
          const combinados = dadosPrecos.map((preco) => {
            const nomeObj = dadosNomes.find(n => String(n.id) === String(preco.emolumento_nome_id || preco.nome_id)) || {};
            const naturezaObj = dadosNaturezas.find(nat => String(nat.id) === String(nomeObj.emolumento_natureza_id || nomeObj.natureza_id)) || {};

            return {
              id: preco.id,
              valor: preco.valor || preco.preco || "0",
              referencia: preco.referencia || preco.descricao || "Item",
              nome: nomeObj.nome || nomeObj.descricao || preco.nome || "Nome não especificado",
              naturezaId: naturezaObj.id || nomeObj.emolumento_natureza_id || nomeObj.natureza_id || "",
              natureza: naturezaObj.nome || naturezaObj.descricao || "Geral"
            };
          });

          setItensCompletos(combinados);
        }
      } catch (error) {
        console.error("Erro ao carregar dados dos emolumentos:", error);
      }
    };

    fetchData();
    return () => { isMounted = false; };
  }, []);

  // Filtragem local baseada na barra de pesquisa e na natureza selecionada
  const itensFiltrados = itensCompletos.filter(item => {
    const termo = searchTerm.toLowerCase();
    const matchTermo = (
      item.nome.toLowerCase().includes(termo) ||
      item.referencia.toLowerCase().includes(termo) ||
      item.natureza.toLowerCase().includes(termo)
    );

    const matchNatureza = selectedNaturezaId === "" || String(item.naturezaId) === String(selectedNaturezaId);

    return matchTermo && matchNatureza;
  });

  return (
    <main className="bg-white text-dark">
      <Header />

      {/* Hero Catalog Section */}
      <section className="bg-black text-white py-5 position-relative overflow-hidden">
        <div className="container py-4 text-center position-relative z-2">
          <span className="text-warning fw-bold text-uppercase tracking-wider small mb-2 d-block">
            Início / Catálogo
          </span>
          <h1 className="display-4 fw-bold mb-3">
            Emolumentos & <span className="text-warning">Preços</span>
          </h1>
          <p className="lead text-light opacity-75 mx-auto mb-4" style={{ maxWidth: '700px' }}>
            Consulte a tabela completa de emolumentos e valores integrados diretamente no sistema.
          </p>
        </div>
      </section>

      {/* Search & Filter Bar */}
      <section className="py-4 bg-light border-bottom">
        <div className="container">
          <div className="row g-3 align-items-center">
            <div className="col-lg-8">
              <div className="input-group">
                <span className="input-group-text bg-white border-end-0 text-muted"><FaSearch /></span>
                <input 
                  type="text" 
                  className="form-control border-start-0 shadow-none" 
                  placeholder="Pesquisar por nome, referência ou natureza..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            
            <div className="col-lg-4">
              <select 
                className="form-select bg-white"
                value={selectedNaturezaId}
                onChange={(e) => setSelectedNaturezaId(e.target.value)}
              >
                <option value="">Todas as Naturezas</option>
                {naturezas.map((nat) => (
                  <option key={nat.id} value={nat.id}>
                    {nat.nome || nat.descricao || `Natureza ${nat.id}`}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <section className="py-5">
        <div className="container">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <span className="text-muted small">Mostrando emolumentos filtrados por natureza</span>
            <span className="badge bg-dark text-warning p-2">Total: {itensFiltrados.length} registos</span>
          </div>

          <div className="row g-4">
            {itensFiltrados.length > 0 ? (
              itensFiltrados.map((item, index) => (
                <div key={item.id || index} className="col-md-6 col-lg-4">
                  <div className="card h-100 border-0 shadow-sm rounded-4 overflow-hidden bg-light">
                    <div className="p-4 bg-dark text-white d-flex flex-column justify-content-center" style={{ minHeight: '140px' }}>
                      <span className="badge bg-warning text-dark align-self-start mb-2 px-2 py-1 fw-bold" style={{ fontSize: '0.65rem' }}>{item.natureza}</span>
                      <h5 className="fw-bold mb-1">{item.nome}</h5>
                      <span className="text-warning fw-semibold">{item.valor} Kz</span>
                    </div>
                    <div className="card-body p-4 d-flex flex-column">
                      <p className="text-muted small mb-0">Ref: {item.referencia}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-12 text-center py-5 bg-light rounded-4 border">
                <h5 className="text-dark fw-bold mb-2">Nenhum registo encontrado</h5>
                <p className="text-muted small mb-0">Tente ajustar os filtros ou pesquisar por outro termo.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Catalogo;